import axios, {
  type AxiosError, type AxiosRequestConfig, type AxiosResponse, type InternalAxiosRequestConfig,
} from 'axios'
import { ApiError, type Envelope } from './types'
import { CODE, SILENT_CODES } from '@/constants/errorCodes'

const TOKEN_KEY = 'zr.portal.token'

/**
 * token 存哪儿取决于登录页的「在此设备上保持登录」（需求 §5.1）：
 *   勾了   → localStorage，关掉浏览器仍在
 *   没勾   → sessionStorage，关掉标签页即失效
 * 读的时候两处都看，写的时候只写一处，避免两边留下过期副本。
 */
export const tokenStore = {
  get(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY) ?? sessionStorage.getItem(TOKEN_KEY)
    } catch {
      return null
    }
  },
  set(token: string, persist: boolean) {
    try {
      localStorage.removeItem(TOKEN_KEY)
      sessionStorage.removeItem(TOKEN_KEY)
      ;(persist ? localStorage : sessionStorage).setItem(TOKEN_KEY, token)
    } catch {
      /* 隐私模式下写不进去，登录仍可用，只是刷新会掉登录态 */
    }
  },
  /**
   * 当前 token 是不是"持久"存的（登录时勾了「在此设备上保持登录」）。
   *
   * 改密时后端会换发新 token（4.0 起本会话保留），换上去的时候得**放回原来的地方** ——
   * 一律写 localStorage 会把没勾保持登录的人悄悄变成长期保存，
   * 一律写 sessionStorage 又会让勾了的人关掉浏览器就掉登录态。
   */
  isPersistent(): boolean {
    try {
      return localStorage.getItem(TOKEN_KEY) != null
    } catch {
      return false
    }
  },
  clear() {
    try {
      localStorage.removeItem(TOKEN_KEY)
      sessionStorage.removeItem(TOKEN_KEY)
    } catch {
      /* ignore */
    }
  },
}

/** 兜底值＝系统默认语言。`main.ts` 挂载前就会 `setLocaleGetter`，这里只覆盖那之前的极早期请求 */
let currentLocale = () => 'en-AU'
export function setLocaleGetter(fn: () => string) {
  currentLocale = fn
}

/**
 * 没有后端信封可用时（断网 / 超时 / 网关回了非 JSON 的 5xx）给客户看的那句话 —— 按当前界面语言取。
 *
 * 不能直接 `import { t } from '@/locales'`：locales/index 建 i18n 实例时会把整个语言包拉进来，
 * 而本文件是所有 api 模块的根依赖，循环引用会让首屏多拉一轮；所以这里只放三句话，与语言包 `common.*` 同文。
 * 以前这里塞的是 axios 的英文原文（`timeout of 20000ms exceeded`）或写死的中文。
 */
const NETWORK_MESSAGES: Record<string, { offline: string; timeout: string; http: (status: number) => string }> = {
  'zh-CN': {
    offline: '网络异常，请稍后重试',
    timeout: '请求超时，请稍后重试',
    http: (s) => `服务暂时不可用（HTTP ${s}），请稍后重试`,
  },
  'zh-HK': {
    offline: '網絡異常，請稍後重試',
    timeout: '請求逾時，請稍後重試',
    http: (s) => `服務暫時無法使用（HTTP ${s}），請稍後重試`,
  },
  'en-AU': {
    offline: 'Network problem. Please try again.',
    timeout: 'The request timed out. Please try again.',
    http: (s) => `The service is temporarily unavailable (HTTP ${s}). Please try again.`,
  },
}

/** -1 错误的文案：超时 / 有状态码但没有信封 / 完全没连上，三种分开说 */
export function networkErrorMessage(ax: AxiosError): string {
  const m = NETWORK_MESSAGES[currentLocale()] ?? NETWORK_MESSAGES['en-AU']!
  if (ax.code === 'ECONNABORTED' || ax.code === 'ETIMEDOUT') return m.timeout
  if (ax.response?.status) return m.http(ax.response.status)
  return m.offline
}

interface Hooks {
  /** 非静默错误的统一提示 */
  onError?: (e: ApiError) => void
  /** 401 / 40101 / 40111 / 40313：未登录或登录失效、会话已吊销（含被顶下线）、登录已停用 */
  onUnauthorized?: (e: ApiError) => void
  /** 40301：密码已过期 / 首登未改密，必须先改密（R-18）。返回 Promise 便于先拉 /profile/me 再跳页 */
  onMustChangePassword?: () => void | Promise<void>
  /** 40311 / 40312：portal 闸拦截，回首页并弹对应弹窗。带上请求 url —— 排查「谁把人踢回概览」全靠它 */
  onGateDenied?: (e: ApiError, url?: string) => void
}
let hooks: Hooks = {}
export function setHttpHooks(h: Hooks) {
  hooks = h
}

export const http = axios.create({
  baseURL: import.meta.env.VITE_API_BASE || '/api',
  timeout: 20_000,
})

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.get()
  if (token) config.headers.set('Authorization', `Bearer ${token}`)
  // 后端据此渲染邮件与题库的语言
  config.headers.set('Accept-Language', currentLocale())
  return config
})

/**
 * 登录接口的 401 是「账号或密码错误」，不是会话失效 —— 不能清 token 跳登录页
 * （否则在登录页输错密码会原地"跳转"一次并丢掉提示）。其余端点的 401 一律视为未登录。
 */
function isLoginEndpoint(url?: string): boolean {
  return !!url && /\/c\/auth\/login$/.test(url)
}

/**
 * 请求选项。`silent`：失败时不弹全局 toast——给**后台刷新**用（轮询、SDK 事件触发的重拉）：
 * 页面上已经有一份数据，这一次没拉到等下一轮就是，弹出来只是噪音；手机上切到相机 / 后台时
 * 在途请求被系统掐断是常态，认证页在 SDK 拍摄过程中会连弹「网络异常」（2026-10-14 dev 截图）。
 * 只压 toast：登录失效、强制改密、门禁拦截照常处理，那些不是「这一轮没拉到」。
 */
export interface RequestOptions extends AxiosRequestConfig {
  silent?: boolean
  /**
   * 只对这几个业务码不弹全局 toast——调用方要把它们就地显示在表单上（比 `silent` 窄：网络错误、其余业务码照常弹）。
   * 与 `silent` 一样只压 toast，登录失效 / 强制改密 / 门禁拦截照常处理。用法见补填手机号（D-98）。
   */
  inline?: readonly number[]
}

function dispatch(e: ApiError, url?: string, silent = false): never {
  if (
    e.code === CODE.UNAUTHORIZED ||
    // 4.0 补：会话被吊销（改密、账号禁用、互踢 / 被顶下线），同样要回登录页
    e.code === CODE.SESSION_REVOKED ||
    // 40313：会话中途账户被停用。后端 CustomerSessionGuard 已吊销会话，再留在页面上下一次请求照样被踢
    e.code === CODE.LOGIN_DISABLED ||
    // 裸 401：网关 / 服务拦截器在 token 缺失或失效时的回法（2026-09-07 起后端改发 40101，这里保留兼容）
    (e.code === CODE.UNAUTHENTICATED && !isLoginEndpoint(url))
  ) {
    hooks.onUnauthorized?.(e)
  }
  else if (e.code === CODE.MUST_CHANGE_PWD) void hooks.onMustChangePassword?.()
  else if (e.code === CODE.PORTAL_GATE || e.code === CODE.ACTIVATION_LOCKED) hooks.onGateDenied?.(e, url)
  else if (!silent && !SILENT_CODES.includes(e.code)) hooks.onError?.(e)
  throw e
}

/**
 * 解包统一信封。`code !== 0` 一律抛 ApiError——
 * 让「调了就当成功」这种写法在类型层面就不成立。
 */
export async function unwrap<T>(
  p: Promise<AxiosResponse<Envelope<T>>>,
  silent = false,
  inline: readonly number[] = [],
): Promise<T> {
  const quiet = (code: number) => silent || inline.includes(code)
  try {
    const res = await p
    const body = res.data
    if (body && typeof body === 'object' && 'code' in body) {
      if (body.code !== 0) dispatch(new ApiError(body.code, body.message, body.traceId), res.config?.url, quiet(body.code))
      return body.data
    }
    return body as unknown as T
  } catch (err) {
    if (err instanceof ApiError) throw err
    // 调用方主动取消（查询带 signal、被 invalidate 换下或页面卸载）：没有失败可言，原样抛给 TanStack，不弹 toast。
    // 不判这一条的话，取消会落进下面「没有信封」的分支，被说成「网络异常」
    if (axios.isCancel(err)) throw err
    const ax = err as AxiosError<Envelope<unknown>>
    const payload = ax.response?.data
    // 后端在 401/403/422 等状态码上同样返回信封，优先用信封里的码与文案
    if (payload && typeof payload === 'object' && 'code' in payload) {
      dispatch(new ApiError(payload.code, payload.message, payload.traceId), ax.config?.url, quiet(payload.code))
    }
    // 没有信封：按界面语言给文案（原来是 axios 英文原文 / 写死中文）。-1 不在 SILENT_CODES 里，会走 onError 弹 toast
    dispatch(new ApiError(-1, networkErrorMessage(ax)), undefined, silent)
  }
}

export const api = {
  get: <T>(url: string, params?: unknown, { silent, inline, ...config }: RequestOptions = {}) =>
    unwrap<T>(http.get<Envelope<T>>(url, { params, ...config }), silent, inline),
  post: <T>(url: string, body?: unknown, { silent, inline, ...config }: RequestOptions = {}) =>
    unwrap<T>(http.post<Envelope<T>>(url, body, config), silent, inline),
  put: <T>(url: string, body?: unknown, { silent, inline, ...config }: RequestOptions = {}) =>
    unwrap<T>(http.put<Envelope<T>>(url, body, config), silent, inline),
  del: <T>(url: string, { silent, inline, ...config }: RequestOptions = {}) =>
    unwrap<T>(http.delete<Envelope<T>>(url, config), silent, inline),
}

/* ==================== 文件流下载（报表导出等） ==================== */

/** Blob 读成文本：jsdom 等老环境的 Blob 没有 text()，退回 Response */
function blobText(b: Blob): Promise<string> {
  return typeof b.text === 'function' ? b.text() : new Response(b).text()
}

/**
 * 从响应体里认出统一信封。`responseType: 'blob'` 时，后端的 JSON 信封（422 / 401 …）也会被 axios 包成 Blob，
 * 不读出来就拿不到业务码与文案——客户只会看到一句「服务暂不可用」；mock 的失败则是 200 + 普通对象。两种都认。
 */
async function envelopeOf(body: unknown): Promise<Envelope<unknown> | null> {
  let v: unknown = body
  if (body instanceof Blob) {
    if (!body.type.includes('json')) return null
    try {
      v = JSON.parse(await blobText(body))
    } catch {
      return null
    }
  }
  return v && typeof v === 'object' && !(v instanceof Blob) && 'code' in v ? (v as Envelope<unknown>) : null
}

/**
 * `Content-Disposition` 里的文件名：优先 RFC 5987 的 `filename*=UTF-8''…`，其次 `filename="…"` / `filename=…`。
 * 取不到返回 null，由调用方给兜底名。
 */
export function fileNameFromDisposition(cd: string | null | undefined): string | null {
  if (!cd) return null
  const star = /filename\*\s*=\s*utf-8''([^;]+)/i.exec(cd)
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"(.*)"$/, '$1'))
    } catch {
      /* 编码坏了就退回普通 filename */
    }
  }
  const plain = /filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i.exec(cd)
  const name = (plain?.[1] ?? plain?.[2])?.trim()
  return name || null
}

export interface Downloaded {
  blob: Blob
  /** 后端 Content-Disposition 给的文件名；没给为 null */
  fileName: string | null
}

/**
 * GET 一个文件流。失败与 `api.*` 走同一套分发（登录失效踢回登录页、门禁、非静默码弹 toast），
 * 所以调用方只管成功分支，catch 里不必再提示。
 */
export async function download(url: string, params?: unknown): Promise<Downloaded> {
  let res: AxiosResponse<unknown>
  try {
    res = await http.get<unknown>(url, { params, responseType: 'blob' })
  } catch (err) {
    if (axios.isCancel(err)) throw err
    const ax = err as AxiosError
    const env = await envelopeOf(ax.response?.data)
    if (env) dispatch(new ApiError(env.code, env.message, env.traceId), ax.config?.url)
    dispatch(new ApiError(-1, networkErrorMessage(ax)))
  }
  // 200 也可能回信封（mock 的业务失败）：是信封就不是文件
  const env = await envelopeOf(res.data)
  if (env) dispatch(new ApiError(env.code === 0 ? -1 : env.code, env.message, env.traceId), url)
  if (!(res.data instanceof Blob)) {
    dispatch(new ApiError(-1, (NETWORK_MESSAGES[currentLocale()] ?? NETWORK_MESSAGES['en-AU']!).offline))
  }
  return {
    blob: res.data,
    fileName: fileNameFromDisposition(String(res.headers?.['content-disposition'] ?? '')),
  }
}
